/**
 * Admin → Workflow Engine Analytics (Session 4).
 *
 * Operational dashboards on top of the live engine state.
 *
 * Surfaces:
 *   - Overview: definitions by state, runs by status, top-active workflows,
 *     30-day completion rate, average run duration.
 *   - Per-definition funnel: step-level entered / exited / currently-in /
 *     drop-off with median dwell.
 *   - Per-definition bottlenecks: historical (median > expected) + live
 *     stuck runs.
 *   - Event firehose summary: counts by type / source / 7-day window.
 *
 * Route: /admin/workflow-engine/analytics
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import {
  Activity, AlertTriangle, ArrowLeft, BarChart3, RefreshCw,
  TrendingDown, TrendingUp, Zap,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fmtDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

function StatCard({ label, value, sublabel, tone = "slate", testId }) {
  const toneCls = {
    slate: "bg-white border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    blue: "bg-blue-50 border-blue-200",
    rose: "bg-rose-50 border-rose-200",
    amber: "bg-amber-50 border-amber-200",
  }[tone] || "bg-white border-slate-200";
  return (
    <div data-testid={testId} className={`rounded-2xl border ${toneCls} p-4`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
      {sublabel && <div className="mt-1 text-[11px] text-slate-500">{sublabel}</div>}
    </div>
  );
}

function HBar({ label, value, max, color = "bg-blue-500", testId }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div data-testid={testId} className="flex items-center gap-3 py-1.5">
      <div className="w-44 text-xs font-medium text-slate-700 truncate">{label}</div>
      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 text-right text-xs font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

export default function AdminWorkflowAnalytics() {
  const [overview, setOverview] = useState(null);
  const [eventsSummary, setEventsSummary] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [selectedDefnId, setSelectedDefnId] = useState("");
  const [funnel, setFunnel] = useState(null);
  const [bottlenecks, setBottlenecks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defnLoading, setDefnLoading] = useState(false);
  const [includeSandbox, setIncludeSandbox] = useState(false);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const [ov, es, dfs] = await Promise.all([
        api.get("/admin/wfe/analytics/overview"),
        api.get("/admin/wfe/analytics/events-summary"),
        api.get("/admin/wfe/definitions"),
      ]);
      setOverview(ov.data);
      setEventsSummary(es.data);
      const defns = (dfs.data?.definitions || []).filter((d) => d.state === "live" || d.state === "draft");
      setDefinitions(defns);
      if (!selectedDefnId && defns[0]) setSelectedDefnId(defns[0].id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadDefnAnalytics = async (defId) => {
    if (!defId) return;
    setDefnLoading(true);
    try {
      const params = { include_sandbox: includeSandbox };
      const [f, b] = await Promise.all([
        api.get(`/admin/wfe/analytics/definitions/${defId}/funnel`, { params }),
        api.get(`/admin/wfe/analytics/definitions/${defId}/bottlenecks`, { params }),
      ]);
      setFunnel(f.data);
      setBottlenecks(b.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDefnLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    if (selectedDefnId) loadDefnAnalytics(selectedDefnId);
  }, [selectedDefnId, includeSandbox]);

  const runsByStatus = useMemo(() => {
    const r = overview?.runs_by_status || {};
    const all = ["running", "completed", "paused", "cancelled", "failed", "pending"];
    const max = Math.max(1, ...all.map((k) => r[k] || 0));
    return all.map((k) => ({ key: k, count: r[k] || 0, max }));
  }, [overview]);

  const defnByState = useMemo(() => {
    const d = overview?.definitions_by_state || {};
    const all = ["draft", "testing", "live", "paused", "archived"];
    const max = Math.max(1, ...all.map((k) => d[k] || 0));
    return all.map((k) => ({ key: k, count: d[k] || 0, max }));
  }, [overview]);

  return (
    <ToolPageShell
      eyebrow="Admin · Workflow Engine"
      title="Analytics & Monitoring"
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/admin/workflow-engine"
            data-testid="wfe-analytics-back"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-300 text-slate-700 rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Engine home
          </Link>
          <button
            type="button"
            onClick={() => {
              loadOverview();
              if (selectedDefnId) loadDefnAnalytics(selectedDefnId);
            }}
            data-testid="wfe-analytics-refresh"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-3 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      }
      intro="Operational visibility into the Workflow Engine. Definition health, run funnels, bottlenecks, and event firehose — all derived from the live audit log."
    >
      {/* Overview stat cards */}
      <div data-testid="wfe-analytics-overview" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard
          testId="stat-completion-rate"
          label="Completion rate (30d)"
          value={`${overview?.completion_rate_30d_pct ?? 0}%`}
          sublabel="Non-sandbox runs completed"
          tone="emerald"
        />
        <StatCard
          testId="stat-runs-7d"
          label="Runs created (7d)"
          value={overview?.activity?.runs_created_last_7d ?? 0}
          sublabel={`${overview?.activity?.runs_created_last_30d ?? 0} in last 30d`}
          tone="blue"
        />
        <StatCard
          testId="stat-avg-duration"
          label="Avg run duration"
          value={fmtDuration(overview?.avg_duration_seconds_30d)}
          sublabel="Across completed runs (30d)"
        />
        <StatCard
          testId="stat-events-7d"
          label="Events received (7d)"
          value={eventsSummary?.events_last_7d ?? 0}
          sublabel={`${eventsSummary?.total_events ?? 0} all-time`}
          tone="amber"
        />
      </div>

      {/* Definitions + Runs status side-by-side */}
      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-blue-700" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Definitions by state</h2>
          </div>
          <div data-testid="wfe-analytics-defn-states" className="space-y-1">
            {defnByState.map((row) => (
              <HBar
                key={row.key}
                testId={`defn-state-${row.key}`}
                label={row.key}
                value={row.count}
                max={row.max}
                color={{
                  draft: "bg-slate-400",
                  testing: "bg-violet-500",
                  live: "bg-emerald-500",
                  paused: "bg-yellow-500",
                  archived: "bg-slate-300",
                }[row.key] || "bg-slate-400"}
              />
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-blue-700" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Production runs by status</h2>
          </div>
          <div data-testid="wfe-analytics-run-statuses" className="space-y-1">
            {runsByStatus.map((row) => (
              <HBar
                key={row.key}
                testId={`run-status-${row.key}`}
                label={row.key}
                value={row.count}
                max={row.max}
                color={{
                  running: "bg-blue-500",
                  completed: "bg-emerald-500",
                  paused: "bg-yellow-500",
                  cancelled: "bg-slate-300",
                  failed: "bg-rose-500",
                  pending: "bg-slate-400",
                }[row.key] || "bg-slate-400"}
              />
            ))}
            <div className="mt-3 text-[11px] text-slate-500">
              Sandbox runs (excluded): <span className="font-mono">{overview?.sandbox_total ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top active workflows */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Top active workflows</h2>
        </div>
        {(overview?.top_active_workflows || []).length === 0 ? (
          <div data-testid="wfe-analytics-active-empty" className="text-xs text-slate-500 italic">
            No running production runs.
          </div>
        ) : (
          <ul data-testid="wfe-analytics-active-list" className="space-y-1">
            {overview.top_active_workflows.map((w) => (
              <li
                key={w.slug}
                data-testid={`active-workflow-${w.slug}`}
                className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-b-0"
              >
                <span className="font-mono text-slate-700">{w.slug}</span>
                <span className="font-bold tabular-nums text-slate-900">{w.running} running</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Per-definition funnel + bottlenecks */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-8">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-blue-700" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Funnel · per definition</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="wfe-analytics-defn-select"
              value={selectedDefnId}
              onChange={(e) => setSelectedDefnId(e.target.value)}
              className="text-xs border border-slate-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="">— Select a definition —</option>
              {definitions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} (v{d.version}, {d.state})
                </option>
              ))}
            </select>
            <label className="text-xs flex items-center gap-1.5 text-slate-700">
              <input
                type="checkbox"
                data-testid="wfe-analytics-sandbox-toggle"
                checked={includeSandbox}
                onChange={(e) => setIncludeSandbox(e.target.checked)}
              />
              Include sandbox
            </label>
          </div>
        </div>

        {defnLoading && <div className="text-xs text-slate-500">Loading…</div>}
        {!defnLoading && funnel && (funnel.steps || []).length === 0 && (
          <div data-testid="wfe-analytics-funnel-empty" className="text-xs text-slate-500 italic">
            No steps defined.
          </div>
        )}
        {!defnLoading && funnel && (funnel.steps || []).length > 0 && (
          <div data-testid="wfe-analytics-funnel-table" className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-left px-3 py-2">Step</th>
                  <th className="text-right px-3 py-2">Entered</th>
                  <th className="text-right px-3 py-2">Exited</th>
                  <th className="text-right px-3 py-2">In step</th>
                  <th className="text-right px-3 py-2">Drop-off</th>
                  <th className="text-right px-3 py-2">Median dwell</th>
                </tr>
              </thead>
              <tbody>
                {funnel.steps.map((s) => (
                  <tr
                    key={s.step_id}
                    data-testid={`funnel-row-${s.step_id}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-3 py-2 text-xs text-slate-600">{s.stage_name}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-900">{s.step_name}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums text-blue-700 font-bold">{s.entered}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{s.exited}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{s.currently_in}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums text-rose-700 font-semibold">{s.drop_off}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums">{fmtDuration(s.median_dwell_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bottlenecks */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Bottlenecks</h2>
        </div>
        {!bottlenecks ? (
          <div className="text-xs text-slate-500">Select a definition to see bottlenecks.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">
                Historical (median &gt; expected)
              </div>
              {(bottlenecks.historical_bottlenecks || []).length === 0 ? (
                <div data-testid="wfe-bottlenecks-historical-empty" className="text-xs text-emerald-700">No historical bottlenecks.</div>
              ) : (
                <ul data-testid="wfe-bottlenecks-historical" className="space-y-2">
                  {bottlenecks.historical_bottlenecks.map((b) => (
                    <li
                      key={b.step_id}
                      data-testid={`historical-bottleneck-${b.step_id}`}
                      className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs"
                    >
                      <div className="font-semibold text-rose-900">{b.step_name}</div>
                      <div className="text-rose-700 mt-0.5">
                        Median {fmtDuration(b.median_dwell_seconds)} vs expected {b.expected_days}d
                      </div>
                      <div className="text-rose-600 font-bold mt-0.5">+{b.overshoot_pct}% over SLA</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">
                Live (runs stuck right now)
              </div>
              {(bottlenecks.live_bottlenecks || []).length === 0 ? (
                <div data-testid="wfe-bottlenecks-live-empty" className="text-xs text-emerald-700">No runs currently stuck.</div>
              ) : (
                <ul data-testid="wfe-bottlenecks-live" className="space-y-2">
                  {bottlenecks.live_bottlenecks.slice(0, 10).map((b) => (
                    <li
                      key={b.run_id}
                      data-testid={`live-bottleneck-${b.run_id}`}
                      className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs"
                    >
                      <div className="font-semibold text-amber-900">{b.step_name}</div>
                      <div className="text-amber-700 mt-0.5">
                        Subject {b.subject_type} · <span className="font-mono">{b.subject_id}</span>
                      </div>
                      <div className="text-amber-600 font-bold mt-0.5">
                        Elapsed {fmtDuration(b.elapsed_seconds)} · expected {b.expected_days}d · +{b.overshoot_pct}%
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Event summary */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-blue-700" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Event firehose summary</h2>
        </div>
        {!eventsSummary || (eventsSummary.by_type || []).length === 0 ? (
          <div data-testid="wfe-events-summary-empty" className="text-xs text-slate-500 italic">No events received yet.</div>
        ) : (
          <div data-testid="wfe-events-summary" className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">By type</div>
              <ul className="space-y-1">
                {eventsSummary.by_type.map((row) => (
                  <li
                    key={row.event_type}
                    data-testid={`event-type-${row.event_type}`}
                    className="flex items-center justify-between gap-2 text-xs py-1 border-b border-slate-100"
                  >
                    <span className="font-mono text-slate-800">{row.event_type}</span>
                    <span className="text-slate-500">
                      <span className="font-bold tabular-nums text-slate-900">{row.total}</span>
                      <span className="ml-2">{row.last_7d} (7d)</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-2">By source</div>
              {(eventsSummary.by_source || []).length === 0 ? (
                <div className="text-xs text-slate-500">No sources tagged yet.</div>
              ) : (
                <ul className="space-y-1">
                  {eventsSummary.by_source.map((row) => (
                    <li
                      key={row.source}
                      data-testid={`event-source-${row.source}`}
                      className="flex items-center justify-between gap-2 text-xs py-1 border-b border-slate-100"
                    >
                      <span className="font-mono text-slate-800">{row.source}</span>
                      <span className="text-slate-500">
                        <span className="font-bold tabular-nums text-slate-900">{row.count}</span>
                        <span className="ml-2 text-[10px]">last {fmtDate(row.last_received)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      {loading && <div className="text-xs text-slate-500 mt-4">Loading overview…</div>}
    </ToolPageShell>
  );
}
